'use client'

import Link from 'next/link'
import { Logomark } from '@/components/brand'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { badgeText } from '@/lib/review-view'
import { cn } from '@/lib/utils'

/**
 * Header-only shell for a scoped approver persona, restored from
 * Driver-Auto-CC (`frontend/src/components/approver/ApproverShell.tsx`) and
 * generalised (plan 08 WP-VIS-06, plan 04 section 3.1, A13): a navy bar with
 * the brand and product line, one home link with an optional count badge,
 * an actions slot (the notification bell), the signed-in name with a role
 * chip and Sign out. No search, no AI Manager, no pointer glow.
 * Domain-free on purpose: every word comes from the caller.
 *
 * Plan 10 C6: an optional `sidebar` (the approver rail) sits left of the
 * content on large screens and above it on small ones; when it is given, the
 * home link moves into the sidebar and the header keeps the brand, the actions
 * (bell), the name and Sign out.
 */
export function ApproverShell({
  product,
  homeHref,
  homeLabel,
  homeActive = false,
  homeBadge = 0,
  homeBadgeLabel = 'waiting',
  userName,
  roleLabel,
  actions,
  mainId = 'approver-main',
  sidebar,
  sidebarLabel = 'Approver navigation',
  children,
}: {
  /** The product line after "Supervity", e.g. "Accounts Payable". */
  product: string
  homeHref: string
  /** e.g. "My approvals". */
  homeLabel: string
  homeActive?: boolean
  /** Count on the home link (A4 queue badge); hidden at 0. */
  homeBadge?: number
  /** Read after the count by screen readers: "My approvals, 2 waiting". */
  homeBadgeLabel?: string
  userName?: string | null
  /** Shown as a chip so the audience can tell persona windows apart. */
  roleLabel?: string | null
  actions?: React.ReactNode
  mainId?: string
  /** Left rail content (navigation and briefing). */
  sidebar?: React.ReactNode
  /** Accessible name of the sidebar landmark. */
  sidebarLabel?: string
  children: React.ReactNode
}) {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  const badge = badgeText(homeBadge)
  const width = sidebar ? 'max-w-[90rem]' : 'max-w-7xl'

  return (
    // Sticky page toolbars sit below this header (min-h-11 + py-2) with a 12px gap.
    <div className='relative z-10 min-h-screen [--shell-sticky-top:4.5rem]'>
      <a
        href={`#${mainId}`}
        className='sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-toast focus:rounded-md focus:bg-card focus:px-3 focus:py-2 focus:text-sm focus:text-foreground'
      >
        Skip to content
      </a>
      <header className='sticky top-0 z-sticky bg-primary text-primary-foreground shadow-soft'>
        <div className={cn('mx-auto flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 lg:px-8', width)}>
          <Link
            href={homeHref}
            className='flex min-h-11 items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
          >
            <Logomark variant='light' size={24} />
            <span className='font-display text-base font-bold tracking-tight'>Supervity</span>
            <span aria-hidden='true' className='text-white/40'>
              ·
            </span>
            <span className='text-sm text-white/85'>{product}</span>
          </Link>

          {sidebar ? (
            <span className='ml-auto' />
          ) : (
          <nav aria-label='Approver navigation' className='ml-auto flex items-center gap-2'>
            <Link
              href={homeHref}
              aria-current={homeActive ? 'page' : undefined}
              aria-label={badge ? `${homeLabel}, ${badge} ${homeBadgeLabel}` : undefined}
              className={cn(
                'inline-flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower',
                homeActive ? 'bg-white/[0.12] text-white' : 'text-white/80 hover:bg-white/10 hover:text-white'
              )}
            >
              <Icons.checkCircle className='h-4 w-4' aria-hidden='true' />
              {homeLabel}
              {badge && (
                <span
                  aria-hidden='true'
                  className='flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-cornflower px-1.5 text-xs font-semibold text-brand-navy'
                >
                  {badge}
                </span>
              )}
            </Link>
          </nav>
          )}

          {actions}

          {userName && (
            <span className='flex items-center gap-2 text-sm'>
              <span className='max-w-[12rem] truncate text-white'>{userName}</span>
              {roleLabel && (
                <span className='rounded-full bg-white/15 px-2 py-0.5 text-xs font-medium text-white'>{roleLabel}</span>
              )}
            </span>
          )}

          <Button
            asChild
            variant='outline'
            size='sm'
            className='min-h-11 border-white/30 bg-transparent text-white hover:border-white/50 hover:bg-white/10 hover:text-white focus-visible:ring-brand-cornflower focus-visible:ring-offset-primary'
          >
            <a href={`${basePath}/api/auth/logout`}>
              <Icons.logout className='h-4 w-4' aria-hidden='true' />
              Sign out
            </a>
          </Button>
        </div>
      </header>
      {sidebar ? (
        <div className={cn('mx-auto flex flex-col gap-6 px-4 pb-12 pt-6 lg:flex-row lg:px-8', width)}>
          <aside
            aria-label={sidebarLabel}
            className='lg:sticky lg:top-[var(--shell-sticky-top)] lg:max-h-[calc(100vh-var(--shell-sticky-top)-1.5rem)] lg:w-60 lg:shrink-0 lg:self-start lg:overflow-y-auto'
          >
            {sidebar}
          </aside>
          <main id={mainId} tabIndex={-1} className='min-w-0 flex-1 focus:outline-none'>
            {children}
          </main>
        </div>
      ) : (
        <main id={mainId} tabIndex={-1} className='mx-auto max-w-7xl px-4 pb-12 pt-6 focus:outline-none lg:px-8'>
          {children}
        </main>
      )}
    </div>
  )
}
